import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  CircularProgress,
  ListItem,
  ListItemText,
  Stack,
  TextField,
} from "@mui/material";
import React from "react";

export interface ILazyAutocompleteProps<T> {
  isLoading?: boolean;
  value: T | null;
  onChange: (value: T | null) => void;
  data: T[];
  getOptionLabel: (value: T) => string;
  getOptionKey: (value: T) => string;
  getOptionDisabled?: (value: T) => boolean;
  query?: string;
  setQuery?: (query: string) => void;
  isDisabled?: boolean;
  isClearDisabled?: boolean;
  freeSolo?: boolean;
  enableFiltering?: boolean;
  placeholder?: string;
}

export const LazyAutocomplete = <T,>({
  data,
  isLoading,
  getOptionLabel,
  getOptionKey,
  query,
  setQuery,
  isDisabled,
  value,
  onChange,
  isClearDisabled,
  getOptionDisabled,
  freeSolo,
  enableFiltering,
  placeholder,
}: ILazyAutocompleteProps<T>): React.ReactElement<
  ILazyAutocompleteProps<T>
> => {
  return (
    <Autocomplete<T>
      disabled={isDisabled}
      disableClearable={isClearDisabled as any}
      freeSolo={freeSolo as any}
      sx={{
        ".MuiInputBase-root": {
          borderRadius: 0,
          py: 0,
          fontSize: 13,
          fontWeight: 500,
          border: "1px solid #44596a",
          outline: "unset",
          boxShadow: "unset",

          "&:focus": {
            boxShadow: "0 2px 2px 0px rgba(0, 0, 0, 0.3)",
          },
        },
        ".MuiOutlinedInput-notchedOutline": {
          border: "0 !important",
        },
      }}
      value={value}
      onChange={(_, value) => onChange(value)}
      getOptionDisabled={getOptionDisabled}
      options={data}
      filterOptions={enableFiltering ? undefined : (options) => options}
      getOptionLabel={getOptionLabel}
      renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option) => {
        return (
          <ListItem {...props} key={getOptionKey(option)}>
            <Stack alignItems="center" direction="row" sx={{ fontSize: 10 }}>
              <ListItemText
                sx={{
                  my: 0,
                  ".MuiTypography-root": { fontSize: 12, fontWeight: 500 },
                }}
              >
                {getOptionLabel(option)}
              </ListItemText>
            </Stack>
          </ListItem>
        );
      }}
      loading={isLoading}
      inputValue={query}
      onInputChange={(_, value) => {
        if (freeSolo) {
          onChange(value as T);
          return;
        }

        setQuery?.(value);
      }}
      renderInput={(props: AutocompleteRenderInputParams) => (
        <TextField
          {...props}
          variant="outlined"
          slotProps={{
            input: {
              ...props.InputProps,
              placeholder,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {props.InputProps.endAdornment}
                </React.Fragment>
              ),
            },
          }}
        />
      )}
      noOptionsText="Žiadne výsledky"
      loadingText="Načítanie..."
    />
  );
};
